"use client";

import Link from "next/link";

import cn from "classnames";

import {
  buildEcommerceEvent,
  createEcommercePayload,
  mapProductToItem,
  mapProductTypeToAnalyticsCategory,
} from "@/lib/analytics/ecommerce-analytics";
import { setItemListOrigin } from "@/lib/analytics/item-list-context";

import { addRecommendedProductToCart } from "@/app/blu/shop/[slug]/actions";
import { brown } from "@/app/font";
import { EVENTS } from "@/constants/analytics.constants";
import { useTagging } from "@/hooks/useTagging";

import { AddToCartSubmitButton } from "./addToCartSubmitButton.component";
import { ProductCardProduct } from "./productCard.component";
import {
  RECOMMENDED_PRODUCTS_ITEM_LIST_ID,
  RECOMMENDED_PRODUCTS_ITEM_LIST_NAME,
  trackRecommendedProductSelect,
} from "./productCardAnalytics";

export function ProductCardActions({
  className,
  product,
  index,
}: ProductCardActionsProps) {
  const [, setTag] = useTagging();
  const productId = product.id;
  const productSlug = product.slug;
  const productType = product.type;

  const handleSelectItem = () => {
    trackRecommendedProductSelect(product, setTag, index);
  };

  const handleAddToCart = () => {
    if (productType !== "camera") return;

    const productPrice = product.salesPrice ?? product.listPrice ?? 0;
    const monitoringPrice = product.monitoringPlan?.salesPrice ?? 0;

    setItemListOrigin({
      item_list_id: RECOMMENDED_PRODUCTS_ITEM_LIST_ID,
      item_list_name: RECOMMENDED_PRODUCTS_ITEM_LIST_NAME,
      item_id: product.id,
      item_slug: product.slug,
    });

    const items = [
      mapProductToItem(
        {
          id: product.id,
          slug: product.slug,
          name: product.name,
          listPrice: product.listPrice,
          salesPrice: product.salesPrice,
          type: product.type,
        },
        {
          index: 0,
          quantity: 1,
          add_on: "n",
          category: mapProductTypeToAnalyticsCategory(product.type, product.id),
        },
      ),
    ];

    if (product.monitoringPlan?.id) {
      items.push(
        mapProductToItem(
          {
            id: product.monitoringPlan.id,
            name: product.monitoringPlan.name ?? undefined,
            listPrice: product.monitoringPlan.listPrice,
            salesPrice: product.monitoringPlan.salesPrice,
            type: "monitoring",
          },
          {
            index: 1,
            quantity: 1,
            add_on: "n",
            category: "monitoring",
          },
        ),
      );
    }

    const payload = createEcommercePayload(items, {
      value: productPrice + monitoringPrice,
      tax: 0,
      shipping: 0,
      item_list_id: RECOMMENDED_PRODUCTS_ITEM_LIST_ID,
      item_list_name: RECOMMENDED_PRODUCTS_ITEM_LIST_NAME,
      total_price: {
        base: productPrice,
        monitoring: monitoringPrice,
        add_on: 0,
      },
    });

    setTag(buildEcommerceEvent(EVENTS.ADD_TO_CART, payload), { reset: true });
  };

  return (
    <div
      className={cn(
        "hidden min-w-0 shrink-0 flex-col gap-4 md:flex md:flex-row",
        className,
      )}>
      {productId && (
        <form
          action={addRecommendedProductToCart}
          onSubmit={handleAddToCart}
          className="min-w-0 shrink-0">
          <input type="hidden" name="productId" value={productId} />
          <input type="hidden" name="productName" value={product.name} />
          <input type="hidden" name="productType" value={productType ?? ""} />
          <AddToCartSubmitButton productType={productType} />
        </form>
      )}
      {productSlug ? (
        <Link
          href={productSlug ?? "#"}
          onClick={handleSelectItem}
          className={cn(
            brown.className,
            "border-secondary text-secondary focus-visible:ring-secondary flex min-w-0 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full border-2 px-6 py-3 text-sm font-bold whitespace-nowrap uppercase duration-300 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none md:text-[18px]",
          )}>
          View Details
        </Link>
      ) : null}
    </div>
  );
}

export type ProductCardActionsProps = {
  className?: string;
  product: ProductCardProduct;
  index?: number;
};
